import React, { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatPercent } from 'utils/format';

const formatNum = (val, decimals = 1) => {
  if (val == null || isNaN(val)) return '—';
  return Number(val).toFixed(decimals);
};

// Hand-rolled sticky-header table classes (house convention — mirrors data-table).
const TH = 'sticky top-0 z-[3] cursor-pointer select-none whitespace-nowrap border-b border-borderGrey bg-white p-2 font-semibold hover:bg-neutral-50';
const TD = 'border-b border-borderGrey p-2 text-right';
// Totals row is pinned to the bottom of the scroll area — cells need an opaque bg.
const TOTAL_TD = 'sticky bottom-0 z-[2] border-t border-borderGrey bg-neutral-50 p-2 text-right font-semibold';

const COLUMNS = [
  { key: 'league_size', label: 'League Size', align: 'center' },
  { key: 'total_leagues_global', label: 'Total Leagues' },
  { key: 'dfc_user_count', label: 'DFC Users' },
  { key: 'expected_avg_position', label: 'Expected Avg Pos' },
  { key: 'avg_dfc_position', label: 'DFC Avg Position' },
  { key: 'position_improvement', label: 'Pos Improvement' },
  { key: 'avg_global_rank', label: 'Avg Global Rank' },
  { key: 'avg_global_rank_pct', label: 'Global Rank %ile' },
  { key: 'avg_fpl_points_dfc', label: 'DFC Avg Points' },
  { key: 'avg_fpl_points_all', label: 'League Avg Points' },
  { key: 'points_advantage', label: 'Pts Advantage' },
  { key: 'points_advantage_pct', label: 'Pts Advantage %' },
];

// Signed, colored delta cell. Green for improvement, red for decline
// (flipped when invertColor is set).
const ColoredCell = ({ value, decimals = 1, suffix = '', invertColor = false, bold = false, tdClassName = TD }) => {
  if (value == null || isNaN(value)) return <td className={tdClassName}>—</td>;
  const num = Number(value);
  const positive = invertColor ? num < 0 : num > 0;
  const negative = invertColor ? num > 0 : num < 0;
  return (
    <td
      className={cn(
        tdClassName,
        positive && 'text-success',
        negative && 'text-error',
        (num !== 0 || bold) && 'font-semibold'
      )}
    >
      {num > 0 ? '+' : ''}{formatNum(num, decimals)}{suffix}
    </td>
  );
};

export default function LeaguePerformanceTable({ rows = [], summary = {} }) {
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState('desc');

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setOrderBy(field);
      setOrder('desc');
    }
  };

  const sortedRows = useMemo(() => {
    // Derive points_advantage once so it can be both sorted and rendered.
    const withDerived = rows.map((row) => ({
      ...row,
      points_advantage: (row.avg_fpl_points_dfc || 0) - (row.avg_fpl_points_all || 0),
    }));
    if (!orderBy) return withDerived;
    const dir = order === 'asc' ? 1 : -1;
    return [...withDerived].sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last regardless of direction
      if (bv == null) return -1;
      return (Number(av) - Number(bv)) * dir;
    });
  }, [rows, orderBy, order]);

  if (!rows.length) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-6 text-center text-token-md text-muted-foreground">
        No data available for the selected filters.
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded-token-large border border-borderGrey bg-white" style={{ maxHeight: 600 }}>
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            {COLUMNS.map((col) => {
              const active = orderBy === col.key;
              return (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
                  className={cn(TH, col.align === 'center' ? 'text-center' : 'text-right')}
                >
                  <span
                    className={cn(
                      'inline-flex items-center gap-1',
                      col.align === 'center' ? 'justify-center' : 'w-full justify-end'
                    )}
                  >
                    {col.label}
                    {active && (order === 'asc'
                      ? <ArrowUp size={12} aria-hidden="true" />
                      : <ArrowDown size={12} aria-hidden="true" />)}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr key={row.league_size} className="transition-colors hover:bg-neutral-50">
              <td className={cn(TD, 'text-center font-semibold')}>{row.league_size}</td>
              <td className={TD}>{row.total_leagues_global?.toLocaleString()}</td>
              <td className={TD}>{row.dfc_user_count?.toLocaleString()}</td>
              <td className={TD}>{formatNum(row.expected_avg_position)}</td>
              <td className={TD}>{formatNum(row.avg_dfc_position)}</td>
              <ColoredCell value={row.position_improvement} decimals={2} />
              <td className={TD}>
                {row.avg_global_rank != null ? formatNum(row.avg_global_rank, 0) : '—'}
              </td>
              <td
                className={cn(
                  TD,
                  row.avg_global_rank_pct != null && row.avg_global_rank_pct < 50 && 'font-semibold text-success'
                )}
              >
                {formatPercent(row.avg_global_rank_pct)}
              </td>
              <td className={TD}>{formatNum(row.avg_fpl_points_dfc, 0)}</td>
              <td className={TD}>{formatNum(row.avg_fpl_points_all, 0)}</td>
              <ColoredCell value={row.points_advantage} decimals={0} />
              <ColoredCell value={row.points_advantage_pct} decimals={1} suffix="%" />
            </tr>
          ))}

          {/* Totals row — pinned to the bottom of the scroll area */}
          {summary && (
            <tr>
              <td className={cn(TOTAL_TD, 'text-center font-bold')}>Totals</td>
              <td className={TOTAL_TD}>
                {rows.reduce((sum, r) => sum + (r.total_leagues_global || 0), 0).toLocaleString()}
              </td>
              <td className={TOTAL_TD}>{summary.total_memberships?.toLocaleString()}</td>
              <td className={TOTAL_TD}>—</td>
              <td className={TOTAL_TD}>—</td>
              <ColoredCell
                value={summary.overall_position_improvement}
                decimals={2}
                bold
                tdClassName={TOTAL_TD}
              />
              <td className={TOTAL_TD}>—</td>
              <td className={TOTAL_TD}>—</td>
              <td className={TOTAL_TD}>{formatNum(summary.overall_avg_points_dfc, 0)}</td>
              <td className={TOTAL_TD}>{formatNum(summary.overall_avg_points_all, 0)}</td>
              <ColoredCell
                value={(summary.overall_avg_points_dfc || 0) - (summary.overall_avg_points_all || 0)}
                decimals={0}
                bold
                tdClassName={TOTAL_TD}
              />
              <ColoredCell
                value={summary.overall_points_advantage_pct}
                decimals={1}
                suffix="%"
                bold
                tdClassName={TOTAL_TD}
              />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
